import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Button, Tag, Spin, Tooltip } from 'antd';
import { RobotOutlined, CloseOutlined, SendOutlined, DeleteOutlined, ArrowRightOutlined, CheckOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { farmAssistantService } from '../services/farmAssistantService';
import '../styles/farm-assistant.css';

const WELCOME = {
  role: 'assistant',
  content: "Hello! I'm your farm assistant. Ask me about your animals, milk, health, breeding, tasks, stock or this month's finances.",
};

const SUGGESTIONS = ['How many animals do I have?', 'Milk today', 'Overdue tasks', 'Low stock', 'Sick animals'];

// Lists that show what a confirmed action saved, by the start of their query key
const ACTION_QUERY_PREFIXES = {
  record_milk: ['animal-yields', 'milk', 'milking', 'yield', 'real-time-milking-hub', 'animals-for-milking'],
  add_task: ['animal-tasks', 'tasks', 'schedule'],
  record_treatment: ['animal-treatments', 'treatment', 'upcoming-treatments', 'withdrawal'],
};

/**
 * Header toolbar button + chat panel for account owners. Shown only while the super admin has the assistant switched on.
 * The conversation lives in this component only and is cleared on page reload.
 */
const FarmAssistantChat = ({ enabled, buttonClassName = 'header-icon-btn', topOffset = 72 }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [draft, setDraft] = useState('');
  const listRef = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: status } = useQuery({
    queryKey: ['assistant-status'],
    queryFn: farmAssistantService.getStatus,
    enabled,
    staleTime: 5 * 60_000,
  });

  const chatMutation = useMutation({
    mutationFn: farmAssistantService.chat,
    onSuccess: (data) => {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: data.reply,
        links: data.links || [],
        actions: (data.actions || []).map((action) => ({ ...action, state: 'pending' })),
      }]);
    },
    onError: (error) => {
      const text = error.response?.status === 429
        ? 'Too many messages. Please wait a minute and try again.'
        : error.response?.data?.message || 'Sorry, I could not answer right now. Please try again.';
      setMessages((prev) => [...prev, { role: 'assistant', content: text, error: true }]);
    },
  });

  const updateAction = (messageIndex, actionIndex, changes) => {
    setMessages((prev) => prev.map((m, i) => (i !== messageIndex ? m : {
      ...m,
      actions: m.actions.map((a, j) => (j !== actionIndex ? a : { ...a, ...changes })),
    })));
  };

  const actionMutation = useMutation({
    mutationFn: ({ action }) => farmAssistantService.runAction(action),
    onMutate: ({ messageIndex, actionIndex }) => updateAction(messageIndex, actionIndex, { state: 'saving', error: null }),
    onSuccess: (data, { action, messageIndex, actionIndex }) => {
      updateAction(messageIndex, actionIndex, { state: data?.queued ? 'queued' : 'done' });
      const prefixes = ACTION_QUERY_PREFIXES[action.type] || [];
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string'
            && (key === 'animal-timeline' || key === 'animals' || prefixes.some((p) => key.startsWith(p)));
        },
      });
    },
    onError: (error, { messageIndex, actionIndex }) => {
      updateAction(messageIndex, actionIndex, {
        state: 'error',
        error: error.response?.data?.message || error.message || 'Could not save. Please try again.',
      });
    },
  });

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, chatMutation.isPending, open]);

  if (!enabled || !status?.enabled) {
    return null;
  }

  const send = (text) => {
    const message = (text ?? draft).trim();
    if (!message || chatMutation.isPending) return;

    const history = messages
      .filter((m) => m !== WELCOME && !m.error)
      .slice(-10)
      .map(({ role, content }) => ({ role, content }));

    setMessages((prev) => [...prev, { role: 'user', content: message }]);
    setDraft('');
    chatMutation.mutate({ message, history });
  };

  const toolbarButton = (
    <Button
      type="text"
      icon={<RobotOutlined />}
      className={`${buttonClassName}${open ? ' farm-assistant-toggle--open' : ''}`}
      title="Farm assistant"
      onClick={() => setOpen((v) => !v)}
    />
  );

  if (!open) {
    return toolbarButton;
  }

  // Panel is portalled to body so the header's styles don't affect its fixed position
  return (
    <>
    {toolbarButton}
    {createPortal(
    <Card
      className="farm-assistant-panel"
      style={{ top: topOffset + 8 }}
      size="small"
      title={<span><RobotOutlined /> Farm assistant</span>}
      extra={
        <>
          <Tooltip title="Clear chat">
            <Button type="text" size="small" icon={<DeleteOutlined />} onClick={() => setMessages([WELCOME])} />
          </Tooltip>
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setOpen(false)} />
        </>
      }
    >
      <div className="farm-assistant-messages" ref={listRef}>
        {messages.map((m, index) => (
          <div key={index} className={`farm-assistant-msg farm-assistant-msg--${m.role}${m.error ? ' farm-assistant-msg--error' : ''}`}>
            {m.content}
            {m.actions?.map((action, actionIndex) => (
              <div key={actionIndex} className="farm-assistant-action">
                <div>{action.summary}</div>
                {action.error && <div className="farm-assistant-action-error">{action.error}</div>}
                <div className="farm-assistant-action-buttons">
                  {action.state === 'done' && <Tag color="success" icon={<CheckOutlined />}>Saved</Tag>}
                  {action.state === 'queued' && <Tag color="warning">Saved offline, will sync when online</Tag>}
                  {action.state === 'cancelled' && <Tag>Cancelled</Tag>}
                  {['pending', 'saving', 'error'].includes(action.state) && (
                    <>
                      <Button
                        type="primary"
                        size="small"
                        loading={action.state === 'saving'}
                        onClick={() => actionMutation.mutate({ action, messageIndex: index, actionIndex })}
                      >
                        {action.state === 'error' ? 'Try again' : 'Confirm'}
                      </Button>
                      <Button
                        size="small"
                        disabled={action.state === 'saving'}
                        onClick={() => updateAction(index, actionIndex, { state: 'cancelled', error: null })}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {m.links?.length > 0 && (
              <div className="farm-assistant-links">
                {m.links.map((link) => (
                  <Button key={link.path} size="small" icon={<ArrowRightOutlined />} iconPlacement="end" onClick={() => navigate(link.path)}>
                    {link.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        ))}
        {chatMutation.isPending && (
          <div className="farm-assistant-msg farm-assistant-msg--assistant"><Spin size="small" /></div>
        )}
      </div>

      {messages.length === 1 && (
        <div className="farm-assistant-suggestions">
          {SUGGESTIONS.map((s) => (
            <Tag key={s} onClick={() => send(s)}>{s}</Tag>
          ))}
          <Tag onClick={() => setDraft('Tag ')}>Look up a tag</Tag>
        </div>
      )}

      <div className="farm-assistant-input">
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Ask about your farm..."
          autoSize={{ minRows: 1, maxRows: 4 }}
          maxLength={2000}
        />
        <Button type="primary" icon={<SendOutlined />} onClick={() => send()} loading={chatMutation.isPending} />
      </div>
    </Card>,
    document.body
    )}
    </>
  );
};

export default FarmAssistantChat;
